import { Info, KeyboardArrowDown } from '@mui/icons-material';
import {
  FormControl,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrearTable, Executive } from 'lib/api/factoring/types';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import PageHeader from 'lib/designSystem/PageHeader';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import {
  formatMoney,
  formatMoneyBack,
  validatePermissions,
} from 'lib/functions/utils';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import ArrearPopper from '../../../../../common/parameters/components/financeHubForm/components/arrearPopper';
import {
  ConfirmingRequestForCreation,
  OperationDetailsEarlyPayment,
  OperationDetailsTraditional,
  RequestConfiguration,
} from '../../types';
import OperationResume from '../OperationResume';
import SimulationEarlyPayment from './components/SimulationEarlyPayment';
import SimulationTraditional from './components/SimulationTraditional';

const gridStyle = {
  alignContent: 'center',
  alignItems: 'center',
  padding: '4px',
  paddingRight: 23,
};

export const calculateRequestDetailsTraditional = (
  requestForCreation: ConfirmingRequestForCreation,
) => {
  const formData = {
    advance: requestForCreation.advance,
    rate: requestForCreation.rate,
    operationCostComissionFixed:
      requestForCreation.operationCostComissionFixed ||
      requestForCreation.operationCostFixed ||
      0,
    operationCostComissionVariable:
      requestForCreation.operationCostComissionVariable ||
      requestForCreation.operationCostVariable ||
      0,
    expensesCost: requestForCreation.expensesCost || 0,
    arrearType: requestForCreation.arrearType || 'lineal',
    arrearTableId: requestForCreation.arrearTableId || null,
    exchangeDays: requestForCreation.exchangeDays || 0,
    expensesHasIva: requestForCreation.expensesHasIva ?? false,
    fundRate: Number(requestForCreation.fundRate ?? 0),
  };

  let operationAmount = 0;
  let operationAdvanced = 0;
  let operationCostRate = 0;
  const documentsWithDetails = [];
  for (const document of requestForCreation.documents) {
    const documentAmount = document.amount * (document.active ? 1 : 0);
    const { daysToCount } = document;
    const amountFinanced = Math.round(
      documentAmount * (formData.advance / 100),
    );
    const amountRetention = Math.round(
      documentAmount * (1 - formData.advance / 100),
    );
    const amountDocument = documentAmount - amountRetention;
    const costRate = daysToCount
      ? Math.round(
          ((daysToCount + Number(formData.exchangeDays)) / 30) *
            (formData.rate / 100) *
            amountFinanced,
        )
      : 0;
    operationAmount += documentAmount;
    operationAdvanced += amountFinanced;
    operationCostRate += costRate;
    const amountToDeposit = amountFinanced;

    documentsWithDetails.push({
      ...document,
      amountDocument,
      amountFinanced,
      amountRetention,
      amountToDeposit,
      costRate,
    });
  }
  const operationIva = Math.round(
    0.19 *
      (formData.operationCostComissionFixed +
        Math.round(formData.operationCostComissionVariable)),
  );

  const expensesIva = formData.expensesHasIva
    ? Math.round(0.19 * formData.expensesCost)
    : 0;
  const operationToDeposit = operationAdvanced || 0;
  const operationAccountReceivable =
    (formData.operationCostComissionFixed || 0) +
    (formData.operationCostComissionVariable || 0) +
    (formData.expensesCost || 0) +
    (operationCostRate || 0) +
    (operationIva || 0) +
    (expensesIva || 0);
  return {
    advance: formData.advance,
    rate: formData.rate,
    operationAmount,
    operationAdvanced,
    operationCostRate,
    operationToDeposit,
    operationAccountReceivable,
    operationIva,
    expensesIva,
    operationCostComissionFixed: formData.operationCostComissionFixed,
    operationCostComissionVariable: formData.operationCostComissionVariable,
    expensesCost: formData.expensesCost,
    documentsWithDetails,
    arrearType: formData.arrearType,
    arrearTableId: formData.arrearTableId,
    fundRate: formData.fundRate,
  };
};

export const calculateRequestDetailsEarlyPayment = (
  requestForCreation: ConfirmingRequestForCreation,
) => {
  const allocateRoundedTotalByWeights = (
    total: number,
    weights: number[],
  ): number[] => {
    if (!weights.length) return [];

    const normalizedTotal = Math.round(total);
    const totalWeight = weights.reduce((acc, weight) => acc + weight, 0);
    if (totalWeight <= 0) {
      return weights.map(() => 0);
    }

    const allocations = weights.map((weight, index) => {
      const exact = (normalizedTotal * weight) / totalWeight;
      const floorValue = Math.floor(exact);
      return {
        index,
        floorValue,
        remainder: exact - floorValue,
      };
    });

    const result = allocations.map((allocation) => allocation.floorValue);
    let remainderToDistribute =
      normalizedTotal - result.reduce((acc, value) => acc + value, 0);

    allocations
      .sort((a, b) => {
        if (b.remainder !== a.remainder) {
          return b.remainder - a.remainder;
        }
        return a.index - b.index;
      })
      .forEach((allocation) => {
        if (remainderToDistribute <= 0) return;
        result[allocation.index] += 1;
        remainderToDistribute -= 1;
      });

    return result;
  };
  const formData = {
    advance: requestForCreation.advance,
    rate: requestForCreation.rate,
    operationCostComissionFixed:
      requestForCreation.operationCostComissionFixed ||
      requestForCreation.operationCostFixed ||
      0,
    operationCostComissionVariable:
      requestForCreation.operationCostComissionVariable ||
      requestForCreation.operationCostVariable ||
      0,
    expensesCost: requestForCreation.expensesCost || 0,
    arrearType: requestForCreation.arrearType || 'lineal',
    arrearTableId: requestForCreation.arrearTableId || null,
    exchangeDays: requestForCreation.exchangeDays || 0,
    expensesHasIva: requestForCreation.expensesHasIva || false,
    fundRate: requestForCreation.fundRate || 0,
  };

  const operationIva = Math.round(
    0.19 *
      (formData.operationCostComissionFixed +
        Math.round(formData.operationCostComissionVariable)),
  );

  const expensesIva = formData.expensesHasIva
    ? Math.round(0.19 * formData.expensesCost)
    : 0;

  const totalCostComission =
    formData.operationCostComissionFixed +
    formData.operationCostComissionVariable +
    operationIva;

  const totalCostExpenses = formData.expensesCost + expensesIva;

  const documentAmounts = requestForCreation.documents.map(
    (document) => document.amount * (document.active ? 1 : 0),
  );
  const distributedExpenses = allocateRoundedTotalByWeights(
    totalCostExpenses,
    documentAmounts,
  );
  const distributedComission = allocateRoundedTotalByWeights(
    totalCostComission,
    documentAmounts,
  );

  let operationAmount = 0;
  let operationAdvanced = 0;
  let operationCostRate = 0;
  let operationToDeposit = 0;
  const documentsWithDetails = [];
  for (const [index, document] of requestForCreation.documents.entries()) {
    const documentAmount = document.amount * (document.active ? 1 : 0);
    const { daysToCount } = document;
    const amountFinanced = Math.round(
      documentAmount * (formData.advance / 100),
    );
    const amountRetention = Math.round(
      documentAmount * (1 - formData.advance / 100),
    );
    const amountDocument = documentAmount - amountRetention;

    const costRate = daysToCount
      ? Math.round(
          ((daysToCount + Number(formData.exchangeDays)) / 30) *
            (formData.rate / 100) *
            amountFinanced,
        )
      : 0;
    const costExpenses = distributedExpenses[index] ?? 0;
    const costComission = distributedComission[index] ?? 0;

    const amountToDeposit =
      documentAmount - costRate - costExpenses - costComission;

    operationAmount += documentAmount;
    operationAdvanced += amountFinanced;
    operationCostRate += costRate;
    operationToDeposit += amountToDeposit;

    documentsWithDetails.push({
      ...document,
      amountDocument,
      amountFinanced,
      amountRetention,
      amountToDeposit,
      costRate,
      costExpenses,
      costComission,
    });
  }
  return {
    advance: formData.advance,
    rate: formData.rate,
    operationAmount,
    operationAdvanced,
    operationCostRate,
    operationToDeposit,
    operationIva,
    expensesIva,
    operationCostComissionFixed: formData.operationCostComissionFixed,
    operationCostComissionVariable: formData.operationCostComissionVariable,
    expensesCost: formData.expensesCost,
    documentsWithDetails,
    arrearType: formData.arrearType,
    arrearTableId: formData.arrearTableId,
    fundRate: formData.fundRate,
  };
};

export const CustomTooltip = (text: Array<Array<string>>) => (
  <div style={{ padding: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
      <Typography variant="body1">Descripción</Typography>
    </div>
    <Table size="small">
      <TableBody>
        {text.map((row) => (
          <TableRow key={row[0]}>
            <TableCell key={row[0]}>{row[0]}</TableCell>
            <TableCell key={row[1]}>{row[1]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

type SimulatorProps = {
  requestForCreation: ConfirmingRequestForCreation | null;
  setRequestForCreation: (
    requestForCreation: ConfirmingRequestForCreation,
  ) => void;
  availableArrearTables: ArrearTable[];
  requestConfiguration: RequestConfiguration;
  executivesPortfolio: Executive[];
  executivesAll: Executive[];
  onExportSimulation: () => Promise<void>;
};

const Simulator = (props: SimulatorProps) => {
  const {
    requestForCreation,
    setRequestForCreation,
    availableArrearTables,
    requestConfiguration,
    executivesPortfolio,
    executivesAll,
    onExportSimulation,
  } = props;
  if (!requestForCreation) return null;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [openArrearPopper, setOpenArrearPopper] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const [operationDetailsTraditional, setOperationDetailsTraditional] =
    useState<OperationDetailsTraditional>({
      operationAmount: 0,
      operationAdvanced: 0,
      operationCostRate: 0,
      operationToDeposit: 0,
      operationAccountReceivable: 0,
      operationIva: 0,
      expensesCost: 0,
      expensesIva: 0,
      operationCostComissionFixed: 0,
      operationCostComissionVariable: 0,
      advance: 0,
      rate: 0,
      fundRate: 0,
      documentsWithDetails: [],
    });
  const [operationDetailsEarlyPayment, setOperationDetailsEarlyPayment] =
    useState<OperationDetailsEarlyPayment>({
      operationAmount: 0,
      operationAdvanced: 0,
      operationCostRate: 0,
      operationToDeposit: 0,
      operationIva: 0,
      expensesCost: 0,
      expensesIva: 0,
      operationCostComissionFixed: 0,
      operationCostComissionVariable: 0,
      advance: 0,
      rate: 0,
      fundRate: 0,
      documentsWithDetails: [],
    });

  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const inRange = (value: number | string) => {
    return Number(value) >= 0 && Number(value) <= 100;
  };

  const handleChange = (id: string, newValue: string | number) => {
    const moneyIds = ['operationCostComissionFixed', 'expensesCost'];
    const value = moneyIds.includes(id)
      ? formatMoneyBack(newValue.toString()) || 0
      : newValue;
    if (isNaN(Number(value)) && id !== 'arrearType' && id !== 'arrearTableId')
      return;
    if (id === 'exchangeDays' && !Number.isInteger(Number(value))) return;
    if (id === 'advance' && !inRange(value)) return;
    if (id === 'operationCostComissionVariable') {
      if (!inRange(value)) return;
      const variableAmount =
        (requestForCreation?.amount || 0) * (Number(value) / 100);
      return setRequestForCreation({
        ...requestForCreation,
        operationCostComissionVariable: variableAmount,
        operationCostComissionVariablePercentage: value,
      });
    }
    if (id === 'rate' && !inRange(value)) return;
    if (id === 'arrearRate' && !inRange(value)) return;
    if (id === 'fundRate' && !inRange(value)) return;
    return setRequestForCreation({
      ...requestForCreation,
      [id]: value,
    });
  };

  const updateSimulationValues = () => {
    if (requestForCreation.productType === 'traditional') {
      const {
        advance,
        rate,
        operationAmount,
        operationAdvanced,
        operationCostRate,
        operationToDeposit,
        operationAccountReceivable,
        operationIva,
        expensesIva,
        operationCostComissionFixed,
        operationCostComissionVariable,
        expensesCost,
        documentsWithDetails,
        fundRate,
      } = calculateRequestDetailsTraditional(requestForCreation);
      if (
        JSON.stringify(requestForCreation.documents) !==
        JSON.stringify(documentsWithDetails)
      ) {
        setRequestForCreation({
          ...requestForCreation,
          documents: documentsWithDetails,
        });
      }
      setOperationDetailsTraditional({
        operationAmount,
        operationAdvanced,
        operationCostRate,
        operationToDeposit,
        operationAccountReceivable,
        operationIva,
        expensesCost,
        expensesIva,
        operationCostComissionFixed,
        operationCostComissionVariable,
        advance,
        rate,
        fundRate,
        documentsWithDetails,
      });
    } else {
      const {
        advance,
        rate,
        operationAmount,
        operationAdvanced,
        operationCostRate,
        operationToDeposit,
        operationIva,
        expensesIva,
        operationCostComissionFixed,
        operationCostComissionVariable,
        expensesCost,
        documentsWithDetails,
        fundRate,
      } = calculateRequestDetailsEarlyPayment(requestForCreation);
      if (
        JSON.stringify(requestForCreation.documents) !==
        JSON.stringify(documentsWithDetails)
      ) {
        setRequestForCreation({
          ...requestForCreation,
          documents: documentsWithDetails,
        });
      }
      setOperationDetailsEarlyPayment({
        operationAmount,
        operationAdvanced,
        operationCostRate,
        operationToDeposit,
        operationIva,
        expensesCost,
        expensesIva,
        operationCostComissionFixed,
        operationCostComissionVariable,
        advance,
        rate,
        fundRate,
        documentsWithDetails,
      });
    }
  };

  useEffect(() => {
    updateSimulationValues();
  }, [requestForCreation]);

  // Force re-render when executives are loaded
  useEffect(() => {
    // This effect will trigger a re-render when executives are loaded
  }, [executivesPortfolio, executivesAll]);

  const arrearRateFixed = requestConfiguration.fixedArrearRate;

  // Find executive name by ID for display
  const getExecutiveName = (executiveId: string | number) => {
    if (!executiveId) return '';

    // Search in portfolio executives first
    const portfolioExecutive = executivesPortfolio?.find((exec) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (portfolioExecutive) {
      return portfolioExecutive.name;
    }

    // Search in all executives
    const allExecutive = executivesAll?.find((exec) => {
      return (
        exec.id === executiveId ||
        exec.id === Number(executiveId) ||
        String(exec.id) === executiveId
      );
    });
    if (allExecutive) {
      return allExecutive.name;
    }

    return '';
  };

  const canEditExchangeDays = validatePermissions(
    user,
    'financing_confirming_edit_exchange_days',
  );

  return (
    <div>
      <Grid container>
        <Grid item xs={7}>
          <div
            style={{
              paddingRight: '10px',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            }}
          >
            <OperationResume
              stakeholderName={requestForCreation.stakeholderName}
              stakeholderIdentifier={requestForCreation.stakeholderIdentifier}
              productType={requestForCreation.productType}
              requestDate={requestForCreation.requestDate}
            />
            <div style={{ padding: '20px 0px' }}>
              <Grid
                container
                style={gridStyle}
                columnSpacing={3}
                rowSpacing={1}
              >
                <Grid item xs={2.9}>
                  <Typography fontWeight={600}>Ejecutivo</Typography>
                </Grid>
                <Grid item xs={2.9}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography fontWeight={600}>Montos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Anticipo',
                          'Porcentaje a financiar respecto al total del valor de la operación.',
                        ],
                      ])}
                    >
                      <Info
                        style={{
                          marginLeft: 6,
                          fontSize: 14,
                        }}
                      />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={6.2}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontWeight={600}>Tasas</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Interés',
                          'Interés aplicado a la operación. Se descuenta del monto a financiar como diferencia de precio.',
                        ],
                        [
                          'Fondo',
                          'Costo de venta a fondo. Utilizado para cálculo de spread. No afecta a la solicitud.',
                        ],
                        [
                          'Interés mora',
                          'Interés aplicado por cada día de mora en incrementos diarios.',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Seleccionar ejecutivo</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={
                        requestForCreation.userOwnerId ||
                        requestForCreation.userOwner?.id ||
                        ''
                      }
                      onChange={(e) =>
                        handleChange('userOwnerId', e.target.value)
                      }
                      displayEmpty
                      renderValue={(value) => {
                        if (value) {
                          const name = getExecutiveName(value);
                          return name || 'Seleccionar ejecutivo';
                        }
                        return 'Seleccionar ejecutivo';
                      }}
                    >
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Cliente en cartera
                      </ListSubheader>
                      {executivesPortfolio?.map((executive) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                      <ListSubheader sx={{ fontWeight: 'bold' }}>
                        Todos
                      </ListSubheader>
                      {executivesAll?.map((executive) => (
                        <MenuItem key={executive.id} value={executive.id}>
                          {executive.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={2.9}>
                  <Typography variant="body1">Anticipo (%) </Typography>
                  <TextField
                    value={requestForCreation.advance}
                    placeholder="100"
                    type="number"
                    fullWidth
                    disabled={
                      requestForCreation.productType === 'traditional' ||
                      requestForCreation.productType === 'earlyPayment'
                    }
                    style={{ padding: '0px !important' }}
                    onChange={(e) => handleChange('advance', e.target.value)}
                  />
                </Grid>
                <Grid item xs={1.7}>
                  <Typography variant="body1">Interés (%)</Typography>
                  <TextField
                    value={requestForCreation.rate}
                    type="number"
                    fullWidth
                    placeholder="2.2"
                    onChange={(e) => handleChange('rate', e.target.value)}
                  />
                </Grid>
                <Grid item xs={2.1}>
                  <Typography variant="body1">Fondo (%)</Typography>
                  <TextField
                    value={requestForCreation.fundRate}
                    type="number"
                    fullWidth
                    placeholder="0"
                    onChange={(e) => handleChange('fundRate', e.target.value)}
                  />
                </Grid>
                <Grid item xs={2.4}>
                  <Typography variant="body1">Interés mora (%)</Typography>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      value={
                        requestForCreation.arrearType === 'table'
                          ? 'Tabla'
                          : requestForCreation.arrearRate
                      }
                      type={
                        requestForCreation.arrearType === 'table'
                          ? 'text'
                          : 'number'
                      }
                      fullWidth
                      disabled={
                        requestForCreation.arrearType === 'table' ||
                        arrearRateFixed
                      }
                      placeholder="2.2"
                      onChange={(e) =>
                        handleChange('arrearRate', e.target.value)
                      }
                    />
                    {availableArrearTables.length > 0 && (
                      <>
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setOpenArrearPopper(true);
                          }}
                          disabled={arrearRateFixed}
                        >
                          <KeyboardArrowDown />
                        </IconButton>
                        <ArrearPopper
                          openArrearPopper={openArrearPopper}
                          setOpenArrearPopper={setOpenArrearPopper}
                          anchorEl={anchorEl}
                          formData={requestForCreation}
                          handleChange={handleChange}
                          onEditing={true}
                          availableArrearTables={availableArrearTables}
                        />
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={5.8}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      Comisión operación
                    </Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Monto fijo a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                        [
                          'Variable',
                          'Porcentaje a cobrar por la operación. Reducirá el monto a financiar',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={6}>
                      <Typography variant="body1">Fijo ($)</Typography>
                      <TextField
                        value={formatMoney(
                          requestForCreation.operationCostComissionFixed || 0,
                        )}
                        type="text"
                        fullWidth
                        onChange={(e) =>
                          handleChange(
                            'operationCostComissionFixed',
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body1">Variable (%)</Typography>
                      <TextField
                        value={
                          requestForCreation.operationCostComissionVariablePercentage
                        }
                        type="number"
                        fullWidth
                        onChange={(e) =>
                          handleChange(
                            'operationCostComissionVariable',
                            e.target.value,
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Otros gastos</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Fijo',
                          'Valor de cualquier otro gasto en el que se incurra en la operación',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Fijo ($)</Typography>
                  <TextField
                    value={formatMoney(requestForCreation.expensesCost || 0)}
                    type="text"
                    fullWidth
                    onChange={(e) =>
                      handleChange('expensesCost', e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={3.1}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 10,
                    }}
                  >
                    <Typography fontWeight={600}>Canje</Typography>
                    <LightTooltip
                      title={CustomTooltip([
                        [
                          'Dás de canje',
                          'Días a sumar para el cálculo de la diferencia de precio',
                        ],
                      ])}
                    >
                      <Info style={{ marginLeft: 6, fontSize: 14 }} />
                    </LightTooltip>
                  </div>
                  <Typography variant="body1">Días de canje</Typography>
                  <TextField
                    value={requestForCreation.exchangeDays}
                    type="number"
                    fullWidth
                    placeholder="0"
                    onChange={(e) =>
                      handleChange('exchangeDays', e.target.value)
                    }
                    disabled={!canEditExchangeDays}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        </Grid>
        <Grid item xs={5}>
          {requestForCreation.productType === 'traditional' && (
            <SimulationTraditional
              expanded={true}
              onAccordionChange={() => () => {}}
              requestForCreation={requestForCreation}
              operationDetails={operationDetailsTraditional}
              onExportSimulation={onExportSimulation}
            />
          )}
          {requestForCreation.productType === 'earlyPayment' && (
            <SimulationEarlyPayment
              expanded={true}
              onAccordionChange={() => () => {}}
              requestForCreation={requestForCreation}
              operationDetails={operationDetailsEarlyPayment}
              onExportSimulation={onExportSimulation}
            />
          )}
        </Grid>
      </Grid>
      <PageHeader.TitleOnly
        title="Detalle documentos"
        subtitle={`${requestForCreation.documents.length} documento ${requestForCreation.documents.length === 1 ? 'seleccionado' : 's seleccionados'}`}
        variant="subHeader"
        iconType="documents"
      />
      <EnhancedTable
        dataHeaders={[
          {
            key: 'folio',
            label: 'Folio',
            handleAction: onOpenDocumentProfile,
            icon: <Info fontSize="small" />,
          },
          {
            key: 'code',
            label: 'Código Doc.',
          },
          {
            key: 'businessIdentifier',
            label: 'Rut Proveedor',
          },
          {
            key: 'businessName',
            label: 'Proveedor',
          },
          {
            key: 'issuedDateShortString',
            label: 'Emisión',
          },
          {
            key: 'amount',
            label: 'Monto Doc.',
            type: 'money',
          },
          {
            key: 'costRate',
            label: 'Dif. precio',
            type: 'money',
          },
          ...(requestForCreation.productType === 'earlyPayment'
            ? [
                {
                  key: 'costExpenses',
                  label: 'Gastos',
                  type: 'money',
                },
                {
                  key: 'costComission',
                  label: 'Comisión',
                  type: 'money',
                },
                {
                  key: 'amountToDeposit',
                  label: 'A girar',
                  type: 'money',
                },
              ]
            : []),
          {
            key: 'paymentDateShortString',
            label: 'Vencimiento',
          },
          {
            key: 'daysToCount',
            label: 'Días',
          },
        ]}
        finalSumRow
        data={requestForCreation.documents}
        rowsPerPageDefault={5}
      />
    </div>
  );
};

export default Simulator;
